import { Colors } from '@oy/uicore';
import { IconName, SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  Ref,
  forwardRef,
  useState,
} from 'react';
import styles from './IconButton.module.css';

export enum IconButtonType {
  Clear = 'clear',
  Square = 'square',
  Round = 'round',
  SquareOutlined = 'squareOutlined',
}

export enum IconButtonSize {
  ExtraMini = 'extraMini',
  Mini = 'mini',
  Medium = 'medium',
  Large = 'large',
}

export const IconButtonColor = {
  Primary: 'primary',
  Secondary: 'secondary',
  Destructive: 'destructive',
  CustomTheme: 'custom-theme',
} as const;

type ObjectValues<T extends object> = T[keyof T];
export type IconButtonColorType = ObjectValues<typeof IconButtonColor>;

export type IconButtonProps = {
  /** IconButtonType Clear | Square | Rounded | SquareOutlined */
  buttonType?: IconButtonType;
  /** IconButtonSize Mini | Medium \ Large */
  size?: IconButtonSize;
  /** IconButtonColor Primary | Secondary | Destructive */
  color?: IconButtonColorType;
  /** IconName */
  iconName: IconName;
  /** Allow dedupe mechanism, default is false */
  isDedupe?: boolean;
  dedupeValue?: number;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

const ICON_COLORS = {
  [IconButtonColor.Primary]: Colors.BLUE100,
  [IconButtonColor.Secondary]: Colors.BLACK100,
  [IconButtonColor.Destructive]: Colors.RED100,
};

const ICON_SIZES = {
  [IconButtonSize.ExtraMini]: 10,
  [IconButtonSize.Mini]: 12,
  [IconButtonSize.Medium]: 16,
  [IconButtonSize.Large]: 18,
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      buttonType = IconButtonType.Clear,
      size = IconButtonSize.Medium,
      color = IconButtonColor.Primary,
      iconName,
      className,
      isDedupe = false,
      dedupeValue = 500,
      ...props
    }: IconButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const classes = classNames(styles[buttonType], styles[size], styles[color]);
    const [isPressed, setIsPressed] = useState(false);

    const eventClasses = classNames({
      [styles.pressed]: isPressed,
    });

    let iconColor =
      buttonType === IconButtonType.Clear ||
      buttonType === IconButtonType.SquareOutlined
        ? ICON_COLORS[color as keyof typeof ICON_COLORS]
        : Colors.WHITE;

    if (props.disabled) {
      switch (iconColor) {
        case Colors.BLACK100:
          iconColor = Colors.BLACK40;
          break;
        case Colors.BLUE100:
          iconColor = Colors.BLUE80;
          break;
        case Colors.RED100:
          iconColor = Colors.RED80;
          break;
        default:
          iconColor = Colors.WHITE;
      }
    }
    const iconSize = ICON_SIZES[size];

    const handleMouseDown = () => {
      setIsPressed(true);
    };
    const handleMouseUp = () => {
      setIsPressed(false);
    };

    const [isDisabled, setIsDisabled] = useState(false);

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      const runOnClick = () => {
        if (props.onClick) {
          props.onClick(e);
        }
      };

      // Only execute function after timeout is done
      if (isDedupe) {
        runOnClick();
        setIsDisabled(true);
        setTimeout(() => {
          setIsDisabled(false);
        }, dedupeValue); // Adjust the cooldown period as needed
      } else {
        runOnClick();
      }
    };

    const isButtonDisabled = () => {
      return props.disabled || isDisabled;
    };

    return (
      <button
        {...props}
        type="button"
        ref={ref}
        disabled={isButtonDisabled()}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        className={classNames(
          styles.buttonBase,
          classes,
          eventClasses,
          className,
        )}
      >
        <SystemIcon color={iconColor} name={iconName} size={iconSize} />
      </button>
    );
  },
);

export default Object.assign(IconButton, {
  Size: IconButtonSize,
  Type: IconButtonType,
  Color: IconButtonColor,
});
